import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { getAccountConfiguration } from 'src/services/account';
import { useSettings } from './useSettings';
import { ConfigurationDto } from 'src/models/account';
import useImpersonateSessionData from './useImpersonateSessionData';
import { useCallback } from 'react';

export const useAccountConfiguration = () => {
  const auth = useAuth();
  const { settings } = useSettings();
  const { isImpersonating, impersonateData } = useImpersonateSessionData();

  const { data: configData } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['accountConfiguration', auth.user?.profile?.tenantUserId, settings.currentRole],
    queryFn: async () => {
      let roleToLoad = '';

      // if we are impersonating use the impersonated role
      if (isImpersonating) {
        roleToLoad = impersonateData.roleId;
      } else {
        roleToLoad = settings.currentRole || (auth.user?.profile?.defaultRole as string);
      }

      const res = await getAccountConfiguration(roleToLoad);

      return res.data;
    },
    onSuccess: res => {
      sessionStorage.setItem('account-configurations', JSON.stringify(res));
    },
    enabled: typeof window !== 'undefined' && !!auth.user
  });

  /**
   * Get the account configuration from session.
   */
  const getAccountConfigFromSession = useCallback((): ConfigurationDto => {
    if (typeof window === 'undefined') {
      return {};
    }

    const configFromSession = sessionStorage.getItem('account-configurations');

    if (configFromSession) {
      return JSON.parse(configFromSession);
    }

    return {};
  }, []);

  return { getAccountConfigFromSession, configData };
};
